import * as z from 'zod';
import { ALL_ROLES } from '../lib/roles';

/**
 * The current admin user (`GET /admin/auth/me`). Tolerant: an unknown role
 * string is preserved, so a new backend role doesn't crash the app. (UI access
 * comes from permission keys — `entities/access` — not from the role.)
 */
export const sessionUserSchema = z
  .object({
    user_id: z.number(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    role: z.string(),
  })
  .loose();
export type SessionUser = z.infer<typeof sessionUserSchema>;

/** Whether a role string is one of the known RBAC roles. */
export function isKnownRole(role: string): boolean {
  return (ALL_ROLES as readonly string[]).includes(role);
}

/**
 * Login request body — auth-service `POST /api/v1/auth/user`
 * (`{phone, password}` plus the device identity cpanel always sent so the
 * session names a row on the sessions screen).
 */
export const loginCredentialsSchema = z.object({
  phone: z.string().min(1),
  password: z.string().min(1),
  device_id: z.string().optional(),
  device_name: z.string().optional(),
  platform: z.string().optional(),
});
export type LoginCredentials = z.infer<typeof loginCredentialsSchema>;

/** Change-password request body — auth-service `POST /api/v1/auth/change-password`. */
export const changePasswordPayloadSchema = z.object({
  old_password: z.string().min(1),
  new_password: z.string().min(1),
});
export type ChangePasswordPayload = z.infer<typeof changePasswordPayloadSchema>;

/**
 * Login response. cpanel's legacy backend responds `{ token, user }` while
 * the auth service responds `{ access_token, refresh_token }` with NO user
 * object — the role then comes from the JWT payload. Both shapes are accepted
 * here; the api layer normalizes them.
 */
export const loginResponseSchema = z
  .object({
    token: z.string().min(1).optional(),
    access_token: z.string().min(1).optional(),
    refresh_token: z.string().optional(),
    user: sessionUserSchema.optional(),
  })
  .loose()
  .refine((v) => Boolean(v.token ?? v.access_token), {
    message: 'Login response carries no token',
  });
export type LoginResponse = z.infer<typeof loginResponseSchema>;
